import { Howl } from "howler"
import * as Sentry from "@sentry/react"

const globalAudioCache = new Map()

const activeAudios = new Set()
let nativeSrcDescriptor = null

let sentryWarningSent = false
let sentryErrorSent = false

const isDev = process.env.REACT_APP_ENVIORNMENT === "development"

// Diagnostics stay available in any non-production build so the audio registry
// can be inspected from a dev server regardless of REACT_APP_ENVIORNMENT.
const isNonProduction = () => process.env.NODE_ENV !== "production"

// Chrome stops handing out WebMediaPlayer instances past roughly this many
// live media elements, and every audio call after that fails silently.
const WEB_MEDIA_PLAYER_LIMIT = 65

const isStaticAsset = (src) => {
  if (!src) return false
  return src.includes("/static/media/") || src.startsWith("data:audio/")
}

const isReusableAudio = (audio) => Boolean(audio?.__reusable)

const logDebug = (msg, style) => {
  if (isDev) {
    if (style) console.log(msg, style)
    else console.log(msg)
  }
}

/**
 * Mark an element as owned by a pool that will replay it.
 *
 * Blanking `src` is the only way to actually free a WebMediaPlayer, but it also
 * leaves the element permanently unplayable. Reusable elements are therefore
 * never blanked unless a caller explicitly forces it, which is what caused
 * pooled sounds to stop working in the past.
 */
export const registerReusableAudio = (audio) => {
  if (!audio) return audio
  audio.__reusable = true
  audio.__hadSrc = true
  if (!activeAudios.has(audio)) {
    audio.__debugId =
      audio.__debugId || Math.random().toString(36).substring(2, 9)
    audio.__creationUrl = window.location.href
    activeAudios.add(audio)
  }
  return audio
}

export const isReusableAudioElement = (audio) => isReusableAudio(audio)

const cleanupAudio = (audio, force = false) => {
  if (!audio) return false
  if (!force && isReusableAudio(audio)) return false
  try {
    audio.pause()
    audio.src = ""
    audio.removeAttribute("src")
    audio.load()
    return true
  } catch (e) {
    return false
  }
}

const readSrc = (audio) => {
  try {
    const src = nativeSrcDescriptor?.get?.call(audio) || ""
    return src === "null" ? "" : src
  } catch (e) {
    return ""
  }
}

const currentExerciseId = () => {
  try {
    return new URLSearchParams(window.location.search).get("exerciseId") || ""
  } catch (e) {
    return ""
  }
}

const createdInAnotherExercise = (audio, src) => {
  if (isStaticAsset(src) || !audio.__creationUrl) return false
  const exerciseId = currentExerciseId()
  if (!exerciseId) return false
  try {
    const params = new URLSearchParams(audio.__creationUrl.split("?")[1] || "")
    const createdFor = params.get("exerciseId") || ""
    return createdFor !== "" && createdFor !== exerciseId
  } catch (e) {
    return false
  }
}

/**
 * Non-mutating split of the registry. `alive` is the only number that matters
 * for browser limits; `released` entries are already blanked and harmless.
 *
 * `pending` are elements that never carried a source (a React <audio> mounted
 * without one, for example). They hold no media resource, but they must stay
 * tracked or we would go blind to them if a source is attached later.
 */
const classifyAudios = () => {
  const alive = []
  const released = []
  const pruned = []
  const pending = []

  for (const audio of activeAudios) {
    const src = readSrc(audio)

    if (!src) {
      if (audio.__hadSrc) {
        released.push(audio)
      } else {
        pending.push(audio)
      }
      continue
    }

    audio.__hadSrc = true

    if (createdInAnotherExercise(audio, src)) {
      pruned.push(audio)
      continue
    }

    alive.push({ audio, src })
  }

  return { alive, released, pruned, pending }
}

const shortenSrc = (src) => {
  if (!src) return "(no src)"
  if (src.length <= 90) return src
  return `${src.slice(0, 45)}...${src.slice(-40)}`
}

const buildAudioReport = () => {
  const { alive, released, pruned, pending } = classifyAudios()
  const bySrc = new Map()

  alive.forEach(({ audio, src }) => {
    const key = shortenSrc(src)
    const entry = bySrc.get(key) || { src: key, count: 0, reusable: 0 }
    entry.count += 1
    if (isReusableAudio(audio)) entry.reusable += 1
    bySrc.set(key, entry)
  })

  const sources = [...bySrc.values()].sort((a, b) => b.count - a.count)

  return {
    ts: Date.now(),
    url: window.location.href,
    exerciseId: currentExerciseId(),
    tracked: activeAudios.size,
    alive: alive.length,
    released: released.length,
    pruned: pruned.length,
    pending: pending.length,
    reusableAlive: alive.filter(({ audio }) => isReusableAudio(audio)).length,
    duplicatedSources: sources.filter((entry) => entry.count > 1).length,
    limit: WEB_MEDIA_PLAYER_LIMIT,
    usage: Number((alive.length / WEB_MEDIA_PLAYER_LIMIT).toFixed(3)),
    bySrc: sources,
    topOffenders: sources.slice(0, 10),
  }
}

export const getAudioReport = () => buildAudioReport()

export const diffAudioReports = (previous) => {
  const current = buildAudioReport()
  if (!previous) return { current, delta: null }

  const seconds = Math.max(0.001, (current.ts - previous.ts) / 1000)
  const delta = {
    seconds: Number(seconds.toFixed(2)),
    alive: current.alive - (previous.alive || 0),
    released: current.released - (previous.released || 0),
    pruned: current.pruned - (previous.pruned || 0),
    tracked: current.tracked - (previous.tracked || 0),
  }
  delta.alivePerMinute = Number(((delta.alive / seconds) * 60).toFixed(2))

  return { current, delta }
}

/**
 * Drop entries that are already dead from the registry, so `alive` reflects what
 * the browser is actually holding.
 *
 * `destructive: true` additionally blanks `src` on stale non-reusable elements,
 * which is the only way to really release a WebMediaPlayer. It is opt-in because
 * a blanked element can never play again, and pooled elements are skipped even
 * then.
 */
export const cleanupStaleAudios = ({ destructive = false } = {}) => {
  const { released, pruned } = classifyAudios()
  const targets = destructive ? [...released, ...pruned] : released

  let untracked = 0
  let blanked = 0
  let skippedReusable = 0

  targets.forEach((audio) => {
    if (isReusableAudio(audio)) {
      skippedReusable += 1
      return
    }
    if (destructive && cleanupAudio(audio, true)) blanked += 1
    activeAudios.delete(audio)
    untracked += 1
  })

  if (destructive) {
    for (const [src, sound] of [...globalAudioCache.entries()]) {
      if (!src || isStaticAsset(src)) continue
      try {
        sound.off()
        sound.unload()
        globalAudioCache.delete(src)
      } catch (e) {}
    }
  }

  logDebug(
    `%c[Audio Cache] cleanupStaleAudios destructive=${destructive} untracked=${untracked} blanked=${blanked} skippedReusable=${skippedReusable}`,
    "color: #9C27B0; font-weight: bold;"
  )

  return { untracked, blanked, skippedReusable, destructive }
}

export const clearAllSounds = () => {
  logDebug(
    `%c[Audio Cache] Flushing all audio engine registries completely...`,
    "color: #F44336; font-weight: bold; font-size: 12px;"
  )

  for (const [, sound] of globalAudioCache.entries()) {
    try {
      sound.off()
      sound.unload()
    } catch (e) {}
  }
  globalAudioCache.clear()

  for (const audio of [...activeAudios]) {
    try {
      cleanupAudio(audio, true)
    } catch (e) {}
  }
  activeAudios.clear()
}

export const clearCurrentExerciseSoundsOnly = () => {
  logDebug(
    `%c[Audio Cache] Executing selective exclusion cleanup for static assets...`,
    "color: #FF9800; font-weight: bold; font-size: 12px;"
  )

  for (const [src, sound] of globalAudioCache.entries()) {
    if (src && !isStaticAsset(src)) {
      try {
        sound.off()
        sound.unload()
        globalAudioCache.delete(src)
      } catch (e) {}
    }
  }

  for (const audio of [...activeAudios]) {
    try {
      const src = readSrc(audio)
      if (src && !isStaticAsset(src)) {
        cleanupAudio(audio)
        activeAudios.delete(audio)
      }
    } catch (e) {}
  }
}

export const registerAudioElement = (audio) => {
  if (!audio || activeAudios.has(audio)) return
  audio.__debugId =
    audio.__debugId || Math.random().toString(36).substring(2, 9)
  audio.__creationUrl = window.location.href
  audio.__isReactManaged = true
  activeAudios.add(audio)

  logDebug(
    `%c[Audio Cache] Registered React-managed audio: ${
      audio.src?.split("/").pop() || "(no src)"
    }`,
    "color: #4CAF50; font-weight: bold;"
  )
}

export const unregisterAudioElement = (audio) => {
  if (!audio) return
  activeAudios.delete(audio)

  logDebug(
    `%c[Audio Cache] Unregistered audio element`,
    "color: #FF9800; font-weight: bold;"
  )
}

export const getOrCreateSound = (src, options = {}) => {
  if (!src) return null
  if (globalAudioCache.has(src)) return globalAudioCache.get(src)
  return new Howl({ src: [src], ...options })
}

export const initAudioTracking = () => {
  if (typeof window === "undefined") return

  try {
    nativeSrcDescriptor = Object.getOwnPropertyDescriptor(
      HTMLMediaElement.prototype,
      "src"
    )

    const trackAudio = (audio) => {
      if (activeAudios.has(audio)) return

      const id = Math.random().toString(36).substring(2, 9)
      audio.__debugId = id
      audio.__creationUrl = window.location.href

      activeAudios.add(audio)

      if (activeAudios.size === 65 && !sentryWarningSent) {
        sentryWarningSent = true
        Sentry.captureMessage(
          "High number of active audio elements (approaching WebMediaPlayer limit)",
          {
            level: "warning",
            tags: { issue: "WebMediaPlayer_limit_warning" },
            extra: {
              count: activeAudios.size,
              urls: Array.from(activeAudios)
                .map((a) => nativeSrcDescriptor?.get?.call(a))
                .filter(Boolean)
                .slice(-20),
            },
          }
        )
      }
    }

    const OriginalAudio = typeof Audio !== "undefined" ? window.Audio : null
    if (OriginalAudio) {
      window.Audio = function (...args) {
        try {
          const audio = new OriginalAudio(...args)
          trackAudio(audio)
          return audio
        } catch (error) {
          if (
            error?.message?.includes("WebMediaPlayer") &&
            error?.message?.includes("already in existence")
          ) {
            if (!sentryErrorSent) {
              sentryErrorSent = true
              Sentry.captureException(error, {
                tags: { issue: "WebMediaPlayer_limit_reached" },
                extra: { count: activeAudios.size },
              })
            }
          }
          throw error
        }
      }
      window.Audio.prototype = OriginalAudio.prototype
    }

    const originalCreateElement = document.createElement
    document.createElement = function (tagName, options) {
      if (!tagName || tagName.toLowerCase() !== "audio") {
        return originalCreateElement.call(this, tagName, options)
      }

      try {
        const el = originalCreateElement.call(this, tagName, options)
        trackAudio(el)
        return el
      } catch (error) {
        if (
          error?.message?.includes("WebMediaPlayer") &&
          error?.message?.includes("already in existence")
        ) {
          if (!sentryErrorSent) {
            sentryErrorSent = true
            Sentry.captureException(error, {
              tags: { issue: "WebMediaPlayer_limit_reached" },
              extra: { count: activeAudios.size },
            })
          }
        }
        throw error
      }
    }

    window.getActiveAudios = () => {
      const seenSrcs = new Set()
      const list = []

      const currentParams = new URLSearchParams(window.location.search)
      const currentExerciseId = currentParams.get("exerciseId") || ""

      for (const audio of activeAudios) {
        try {
          const src = nativeSrcDescriptor?.get?.call(audio) || ""

          if (src === "" || src === "null") {
            activeAudios.delete(audio)
            continue
          }

          if (
            !isStaticAsset(src) &&
            audio.__creationUrl &&
            currentExerciseId !== ""
          ) {
            const creationUrlParams = new URLSearchParams(
              audio.__creationUrl.split("?")[1] || ""
            )
            const creationExerciseId = creationUrlParams.get("exerciseId") || ""

            if (
              creationExerciseId !== "" &&
              creationExerciseId !== currentExerciseId
            ) {
              activeAudios.delete(audio)
              continue
            }
          }

          const key = src || `__react_${audio.__debugId}`
          if (seenSrcs.has(key)) continue
          seenSrcs.add(key)

          list.push({
            id: audio.__debugId || "player-node",
            audioSrc: src || "(react-audioplayer)",
            appUrl: window.location.href,
            element: {
              __debugId: audio.__debugId || "player-node",
              __creationUrl: audio.__creationUrl || window.location.href,
              _unlocked: true,
            },
          })
        } catch (e) {
          activeAudios.delete(audio)
        }
      }
      return list
    }

    window.getAudioCount = () => window.getActiveAudios()

    // Safe to expose in production: the default pass is pure bookkeeping and
    // never blanks a source. ModuleContext relies on this existing when the
    // exercise changes, otherwise the call silently no-ops.
    window.cleanupStaleAudios = () => cleanupStaleAudios()

    if (isNonProduction()) {
      // Read-only by default: these report on the registry without mutating it,
      // unlike getActiveAudios, which prunes entries as it walks the set.
      window.__audioDiag = () => buildAudioReport()
      window.__audioDiagDiff = (previous) => diffAudioReports(previous)
      window.__audioCleanup = (options) => cleanupStaleAudios(options)
      window.__audioMarkReusable = (audio) => registerReusableAudio(audio)

      logDebug(
        `%c[AudioManager] Diagnostics ready: __audioDiag(), __audioDiagDiff(prev), __audioCleanup({destructive})`,
        "color: #2196F3; font-weight: bold;"
      )
    }
  } catch (err) {
    console.warn("[AudioManager] Failed to initialize audio tracking:", err)
  }
}
